import { IVectorLayerGroupConfig } from "esdm-map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "esdm-map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "esdm-map/dist/layers/vector/VectorLayerStrategy";

import { HighlandRecordType, HighlandRecordSubType} from "./HighlandRecordTypes";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/MapServer/Hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: HighlandRecordType,
        recordSubType: HighlandRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig = null;

        switch (recordType) {
            case HighlandRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;         

            case HighlandRecordType.Event:
                layerConfig = this.Event(opts);
                break;  

            case HighlandRecordType.FlowCountryWorldHeritageSite:
                layerConfig = this.FlowCountryWHS(opts)
                break;
        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case HighlandRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;

                case HighlandRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case HighlandRecordSubType.DesignedLandscape:
                    layerConfig = this.DesignedLandscape(opts);
                    break; 

                case HighlandRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break; 

                case HighlandRecordSubType.HistoricBattlefield:
                    layerConfig = this.HistoricBattlefield(opts);
                    break; 

                case HighlandRecordSubType.HistoricMarineProtectedArea:
                    layerConfig = this.HistoricMarineProtectedArea(opts);
                    break; 
            }
        }

        return layerConfig;
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: HighlandRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 2000,
                //legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 7,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 7,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 6
                    },
                    {
                        layerName: "MonumentPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 6
                    }
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: HighlandRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 2000,               
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 7,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesWFS",
                        minZoom: 7,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 6
                    },
                    {
                        layerName: "EventPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 6
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: HighlandRecordType.Designation,
                subType: HighlandRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: HighlandRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false,polygon:false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 7,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 6
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: HighlandRecordType.Designation,
                subType: HighlandRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: HighlandRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 7,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 6
                    }
                ]
            }, ...opts
        };
    }
 
    private DesignedLandscape = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Designed Landscapes",
                ref: "DesignedLandscape",
                type: HighlandRecordType.Designation,
                subType: HighlandRecordSubType.DesignedLandscape,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: HighlandRecordSubType.DesignedLandscape },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private HistoricBattlefield = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Historic Battlefields",
                ref: "HistoricBattlefield",
                type: HighlandRecordType.Designation,
                subType: HighlandRecordSubType.HistoricBattlefield,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: HighlandRecordSubType.HistoricBattlefield },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationArea",
                type: HighlandRecordType.Designation,
                subType: HighlandRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: HighlandRecordSubType.ConservationArea },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private HistoricMarineProtectedArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Historic Marine Protected Areas",
                ref: "HistoricMarineProtectedArea",
                type: HighlandRecordType.Designation,
                subType: HighlandRecordSubType.HistoricMarineProtectedArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: HighlandRecordSubType.HistoricMarineProtectedArea },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private FlowCountryWHS = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Flow Country World Heritage Site",
                ref: "FlowCountryWorldHeritageSite",
                type: HighlandRecordType.FlowCountryWorldHeritageSite,
                subType: null,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 1000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "FlowCountryPolygonWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }





   



}

